import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FiTrash2, FiPlus, FiEdit2, FiX } from "react-icons/fi";
import { apiRequest, errorMessage } from "../../api";
import IconPicker, { iconsToList } from "../IconPicker";

const inputClass = "w-full px-3 py-1.5 rounded-md bg-white/5 border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light";
const emptyForm = {
  title: "", category: "", client: "", summary: "", description: "", technologies: "",
  completed_on: "", link: "", order: 0, is_active: true,
};

export default function ProjectsSettings() {
  const formRef = useRef(null);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);   // the project being edited, or null when adding
  const [form, setForm] = useState(emptyForm);
  const [cover, setCover] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [busy, setBusy] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const load = useCallback(async () => {
    const { ok, data } = await apiRequest("/core/projects/", { auth: true });
    if (ok) setItems(data);
    else setError("Failed to load projects.");
    return ok ? data : [];
  }, []);

  useEffect(() => { load(); }, [load]);

  const reset = () => {
    setEditing(null);
    setForm(emptyForm);
    setCover(null);
    setGallery([]);
    setFormKey((k) => k + 1);
  };

  const startEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title, category: item.category, client: item.client, summary: item.summary, description: item.description,
      technologies: item.technologies, completed_on: item.completed_on || "", link: item.link, order: item.order, is_active: item.is_active,
    });
    setCover(null);
    setGallery([]);
    setFormKey((k) => k + 1);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggleTech = (key) => {
    const current = iconsToList(form.technologies);
    setForm({ ...form, technologies: (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]).join(",") });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!editing && !cover) return setError("Please choose a cover image.");
    setBusy(true);
    setError("");

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => { if (k !== "completed_on" || v) fd.append(k, v); });
    if (!editing) fd.set("order", items.length + 1);
    if (cover) fd.append("image", cover);
    const res = await apiRequest(editing ? `/core/projects/${editing.id}/` : "/core/projects/", { method: editing ? "PATCH" : "POST", auth: true, body: fd });
    if (!res.ok) {
      setBusy(false);
      return setError(errorMessage(res.data));
    }

    for (const file of gallery) {
      const g = new FormData();
      g.append("project", res.data.id);
      g.append("image", file);
      const up = await apiRequest("/core/project-images/", { method: "POST", auth: true, body: g });
      if (!up.ok) {
        setBusy(false);
        await load();
        return setError(`Project saved, but a gallery image failed: ${errorMessage(up.data)}`);
      }
    }
    setBusy(false);
    await load();
    reset();
  };

  const removeGalleryImage = async (image) => {
    if (!window.confirm("Remove this image from the gallery?")) return;
    await apiRequest(`/core/project-images/${image.id}/`, { method: "DELETE", auth: true });
    const fresh = await load();
    setEditing(fresh.find((p) => p.id === editing.id) || null);
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    await apiRequest(`/core/projects/${item.id}/`, { method: "DELETE", auth: true });
    if (editing?.id === item.id) reset();
    load();
  };

  const coverPreview = cover ? URL.createObjectURL(cover) : editing?.image;

  return (
    <div className="w-full">
      <h1 className="text-2xl font-bold mb-2">Portfolio Projects</h1>
      <p className="text-sm text-gray-400 mb-6">
        The work we have delivered, shown as cards on the Portfolio page. Each project has its own detail page with a gallery,
        the technologies used and a link. The page's banner and body are under "Banner &amp; Body".
      </p>

      {error && <p className="text-red-400 mb-4">{error}</p>}

      <form ref={formRef} key={formKey} onSubmit={submit} className="mb-6 bg-navy border border-navy-border rounded-xl p-6 scroll-mt-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{editing ? `Edit "${editing.title}"` : "Add New Project"}</h2>
          {editing && <button type="button" onClick={reset} className="flex items-center gap-1 text-xs text-gray-400 hover:text-white"><FiX size={14} /> Cancel edit</button>}
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Title *</label>
            <input required className={inputClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Category</label>
            <input className={inputClass} value={form.category} placeholder="e.g. ERP System" onChange={(e) => setForm({ ...form, category: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Client</label>
            <input className={inputClass} value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs text-gray-400 mb-1">Short summary (shown on the card)</label>
          <textarea rows={2} maxLength={300} className={inputClass} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
        </div>

        <div className="mt-4">
          <label className="block text-xs text-gray-400 mb-1">Details (shown on the project page)</label>
          <div className="bg-white text-black rounded-md">
            <ReactQuill theme="snow" value={form.description} onChange={(value) => setForm({ ...form, description: value })} />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs text-gray-400 mb-1">Technologies used</label>
          <IconPicker selected={iconsToList(form.technologies)} onToggle={toggleTech} />
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Project link</label>
            <input type="url" className={inputClass} value={form.link} placeholder="https://..." onChange={(e) => setForm({ ...form, link: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Completed on</label>
            <input type="date" className={inputClass} value={form.completed_on} onChange={(e) => setForm({ ...form, completed_on: e.target.value })} />
          </div>
          {editing && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Order</label>
              <input type="number" className={inputClass} value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} />
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-4">
          <div>
            <p className="text-sm text-gray-300 mb-2">Cover image {editing ? "" : "*"}</p>
            {coverPreview && <img src={coverPreview} alt="Cover" className="w-full max-w-xs h-auto rounded-md border border-navy-border mb-2" />}
            <input type="file" accept="image/*" onChange={(e) => setCover(e.target.files[0] || null)} className="text-xs text-gray-400" />
          </div>
          <div>
            <p className="text-sm text-gray-300 mb-2">Gallery images</p>
            {editing && editing.images.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {editing.images.map((img) => (
                  <div key={img.id} className="relative">
                    <img src={img.image} alt="" className="w-20 h-16 object-cover rounded-md border border-navy-border" />
                    <button type="button" onClick={() => removeGalleryImage(img)} className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-red-500 text-white" aria-label="Remove"><FiX size={11} /></button>
                  </div>
                ))}
              </div>
            )}
            <input type="file" accept="image/*" multiple onChange={(e) => setGallery([...e.target.files])} className="text-xs text-gray-400" />
            {gallery.length > 0 && <p className="text-xs text-gray-500 mt-1">{gallery.length} new image(s) will be added when you save.</p>}
          </div>
        </div>

        <div className="flex items-center gap-6 mt-5">
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Active (visible on the website)
          </label>
          <button disabled={busy} className="ml-auto px-4 py-2 rounded-md bg-brand-gradient text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
            {busy ? "Saving..." : editing ? "Save Changes" : <><FiPlus /> Add Project</>}
          </button>
        </div>
      </form>

      <div className="bg-navy border border-navy-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-gray-400 text-left">
            <tr>
              <th className="px-4 py-3 w-24">Image</th>
              <th className="px-4 py-3">Project</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3 w-16">Order</th>
              <th className="px-4 py-3 w-20">Status</th>
              <th className="px-4 py-3 w-28"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t border-navy-border align-middle">
                <td className="px-4 py-3"><img src={item.image} alt="" className="w-16 h-12 object-cover rounded-md border border-navy-border" /></td>
                <td className="px-4 py-3">
                  <p className="text-white font-medium">{item.title}</p>
                  <p className="text-xs text-gray-500">{item.client || "—"} · {item.images.length} gallery image(s)</p>
                </td>
                <td className="px-4 py-3 text-gray-400">{item.category || "—"}</td>
                <td className="px-4 py-3 text-gray-400">{item.order}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs ${item.is_active ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-gray-500"}`}>{item.is_active ? "Active" : "Hidden"}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => startEdit(item)} className="p-1.5 rounded-md text-accent-light hover:bg-accent-light/10 flex items-center gap-1 text-xs"><FiEdit2 size={14} /> Edit</button>
                    <button onClick={() => remove(item)} className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10" aria-label="Delete"><FiTrash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">No projects yet — add one above.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
